import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ArrowUpRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { juniorSchoolHome } from "../mock";
import Reveal from "./Reveal";

export default function JuniorSchool() {
  const [active, setActive] = useState(0);
  const slides = juniorSchoolHome.carousel;
  const timerRef = useRef(null);

  // auto scroll
  useEffect(() => {
    timerRef.current = setInterval(() => setActive((a) => (a + 1) % slides.length), 4500);
    return () => clearInterval(timerRef.current);
  }, [slides.length]);

  const go = (dir) => {
    clearInterval(timerRef.current);
    setActive((a) => (a + dir + slides.length) % slides.length);
  };

  return (
    <section id="junior" className="relative bg-white py-12 md:py-20 overflow-hidden">
      <div className="max-w-[1200px] mx-auto px-5 md:px-10">
        {/* Centered heading with animated "NEW" */}
        <Reveal>
          <div className="text-center mb-10 md:mb-14">
            <div className="inline-flex items-center gap-3 mb-5">
              <span className="relative inline-flex items-center">
                {/* Pulse rings */}
                <span className="absolute inset-0 rounded-full bg-[var(--gold)] opacity-40 animate-ping" />
                <span
                  className="absolute inset-0 rounded-full bg-[var(--gold)] opacity-25 animate-ping"
                  style={{ animationDelay: "0.8s", animationDuration: "2.2s" }}
                />
                {/* The NEW badge */}
                <span
                  className="relative inline-flex items-center gap-1.5 px-4 py-1.5 md:px-5 md:py-2 rounded-full text-[var(--navy-deep)] font-semibold text-[13px] md:text-[15px] tracking-[0.32em] uppercase shadow-lg"
                  style={{ background: "var(--gold)" }}
                >
                  <Sparkles size={13} strokeWidth={1.8} />
                  New
                </span>
              </span>
              <span className="text-[11px] md:text-[12px] tracking-[0.32em] uppercase text-[var(--gold)]">
                Just Launched
              </span>
            </div>

            <h2 className="font-serif text-[var(--ink)]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              <span className="block text-[44px] sm:text-[60px] md:text-[88px] leading-[0.95] tracking-tight font-light">
                Junior <span className="serif-italic gold-text">Blog</span>
              </span>
            </h2>
            <span className="gold-divider mx-auto" />
            <p className="text-[14.5px] md:text-[16.5px] leading-[1.85] text-[var(--ink)]/75 max-w-[720px] mx-auto">
              {juniorSchoolHome.body}
            </p>
            <p className="text-[12px] md:text-[13px] tracking-[0.06em] text-[var(--muted)] italic mt-4">
              {juniorSchoolHome.meta}
            </p>
          </div>
        </Reveal>

        {/* Centered carousel */}
        <Reveal variant="zoom" delay={100}>
          <div className="relative max-w-[1000px] mx-auto">
            <div className="relative aspect-video overflow-hidden bg-[var(--navy-deep)]">
              {slides.map((s, i) => (
                <img
                  key={i}
                  src={s.src}
                  alt={s.label}
                  loading="lazy"
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ease-in-out ${
                    i === active ? "opacity-100" : "opacity-0"
                  }`}
                />
              ))}
              <div className="absolute inset-0 bg-gradient-to-t from-[rgba(6,13,34,0.65)] via-transparent to-transparent pointer-events-none" />
              <div className="absolute -top-3 -left-3 w-16 h-16 border-l-2 border-t-2 border-[var(--gold)] pointer-events-none" />
              <div className="absolute -bottom-3 -right-3 w-16 h-16 border-r-2 border-b-2 border-[var(--gold)] pointer-events-none" />
              {/* Label */}
              <div className="absolute bottom-5 left-5 right-5 text-center md:text-left">
                <div className="text-[10.5px] md:text-[11px] tracking-[0.32em] uppercase text-[var(--gold-light)]">
                  Concept Design · EDI
                </div>
                <div className="serif text-white text-[22px] md:text-[30px] leading-tight mt-1">
                  {slides[active].label}
                </div>
              </div>
            </div>

            {/* Arrows + caption below */}
            <div className="mt-6 md:mt-7 flex items-center justify-center gap-4 md:gap-6">
              <button
                onClick={() => go(-1)}
                aria-label="Prev"
                className="w-11 h-11 md:w-12 md:h-12 border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--navy-deep)] flex items-center justify-center transition-colors shrink-0"
              >
                <ChevronLeft size={18} />
              </button>

              <div className="flex flex-col items-center gap-2 min-w-0">
                <div className="serif-italic gold-text text-[15px] md:text-[17px] truncate">
                  {slides[active].label}
                </div>
                <div className="flex items-center gap-1.5">
                  {slides.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setActive(i)}
                      aria-label={`Slide ${i + 1}`}
                      className={`h-[2px] transition-all duration-500 ${
                        i === active ? "w-8 bg-[var(--gold)]" : "w-4 bg-[var(--ink)]/20"
                      }`}
                    />
                  ))}
                </div>
              </div>

              <button
                onClick={() => go(1)}
                aria-label="Next"
                className="w-11 h-11 md:w-12 md:h-12 border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--navy-deep)] flex items-center justify-center transition-colors shrink-0"
              >
                <ChevronRight size={18} />
              </button>
            </div>

            <div className="mt-8 md:mt-10 text-center">
              <Link
                to="/junior-school.html"
                className="btn-gold inline-flex items-center gap-2"
                style={{ background: "var(--gold)", color: "var(--navy-deep)", borderColor: "var(--gold)" }}
              >
                Explore Junior School
                <ArrowUpRight size={18} strokeWidth={1.6} />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
